// KPI 卡：标签 / 大数字 / 趋势 chip，可选健康度进度条
import { ArrowUp, ArrowDown, Minus } from 'lucide-react'
import { cn, formatNumber, healthColor } from '@/lib/utils'

interface Props {
  label: string
  value: number | string | null
  delta?: number
  deltaLabel?: string
  unit?: string
  score?: number
  loading?: boolean
  className?: string
}

export default function KPICard({
  label,
  value,
  delta,
  deltaLabel,
  unit,
  score,
  loading = false,
  className,
}: Props) {
  const hasDelta = typeof delta === 'number' && !Number.isNaN(delta)
  const isUp = hasDelta && (delta as number) > 0
  const isDown = hasDelta && (delta as number) < 0
  const isFlat = hasDelta && (delta as number) === 0

  const displayValue =
    typeof value === 'number' ? formatNumber(value) : value ?? '—'

  const scoreColor = typeof score === 'number' ? healthColor(score) : null
  const scorePct = typeof score === 'number' ? Math.max(0, Math.min(100, score)) : null

  return (
    <div
      className={cn(
        'card flex flex-col gap-2 transition-all hover:shadow-md hover:border-ink-300 group',
        loading && 'opacity-60',
        className,
      )}
    >
      <div className="kpi-label">{label}</div>

      <div className="flex items-baseline gap-1.5">
        <span className="kpi-value">{displayValue}</span>
        {unit && <span className="text-base text-ink-500 font-medium">{unit}</span>}
      </div>

      {typeof scorePct === 'number' && scoreColor && (
        <div className="mt-1">
          <div className="h-1.5 w-full rounded-sm bg-ink-100 overflow-hidden">
            <div
              className="h-full rounded-sm transition-all duration-300"
              style={{ width: `${scorePct}%`, backgroundColor: scoreColor }}
              aria-label={`健康度 ${scorePct}`}
            />
          </div>
        </div>
      )}

      {hasDelta && (
        <div className="flex items-center gap-1.5 mt-1">
          <span
            className={cn(
              'inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium num transition-all',
              isUp && 'bg-primary-50 text-primary-600',
              isDown && 'bg-danger/10 text-danger',
              isFlat && 'bg-ink-50 text-ink-500',
            )}
          >
            {isUp && <ArrowUp size={12} />}
            {isDown && <ArrowDown size={12} />}
            {isFlat && <Minus size={12} />}
            <span>
              {isUp ? '+' : ''}
              {typeof delta === 'number' ? delta.toFixed(1) : delta}
              {deltaLabel ? ` ${deltaLabel}` : ''}
            </span>
          </span>
        </div>
      )}
    </div>
  )
}
